import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  X, History, Search, Calendar, FileText, Copy, Eye, Trash2, 
  Check, ChevronDown, ChevronRight, Clock, Filter, Download, CheckCircle, User, CheckSquare, Square, ListChecks,
  ArrowDownAZ, ArrowUpZA, CalendarSearch
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { toast } from 'sonner';
import { getHistoryDisplayTitle, getDocTypeLabel, formatHistoryTime } from '../../hooks/historyDisplayUtils';
import { StoplightPill, setEncounterStatus } from './EncounterStoplight';
import { HistoryTodoList } from './HistoryTodoList';
import axios from 'axios';

/**
 * HistorySlidePanel - Full-page slide-in panel for viewing all history
 * Slides in from the right side of the screen
 * Provides a less crowded view than the inline history section
 */
export const HistorySlidePanel = ({
  isOpen,
  onClose,
  history,
  loadHistoryItem,
  deleteHistoryItem,
  historyFilter,
  setHistoryFilter,
  historySearchQuery,
  setHistorySearchQuery,
  batchSelectedItems,
  toggleBatchSelect,
  batchDeleteItems,
  markDraftsAsCompleted,
  updateHistoryItem,
  loadHistory,
  effectiveRole,
  textSizeLevel = 0,
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [providerFilter, setProviderFilter] = useState('all');
  // Date-jump filter (from the calendar input). When set, supersedes Today/Week.
  const [dateJump, setDateJump] = useState('');
  // Sort order for history rows — newest by default; toggle to oldest first
  // for chronological review without entering bulk mode.
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest) | 'asc' (oldest)
  const [providers, setProviders] = useState([]);
  const [untaggedCount, setUntaggedCount] = useState(0);
  // Bulk-action mode (separate from per-row Submitted-to-Genesis checkboxes)
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  // Mar 3, 2026 — Career-transition archive UI state. Shows a "Start a new
  // era" pill that lets the provider bulk-archive their previous role's
  // history (e.g. Primary Care) when moving to a new clinic (e.g. Acute
  // Soldier Care). Fully recoverable via the Archived panel.
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveEraLabel, setArchiveEraLabel] = useState('Primary Care era');
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedEras, setArchivedEras] = useState([]);
  const [showArchivedPanel, setShowArchivedPanel] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Fetch distinct provider names
  const API_BASE = window.__WR_API_BASE__;
  // Mar 3, 2026 — Guard against noisy 401s in guest patient mode. Only
  // fire auth-required fetches when we have evidence of a session (cookie
  // or a __WR_USER__ ref). Guests using patient mode don't need any of
  // this data — the History drawer is a provider tool.
  const hasSession = React.useMemo(() => {
    try {
      const c = (typeof document !== 'undefined') ? (document.cookie || '') : '';
      return /session|auth|writeright_/i.test(c) || !!window.__WR_USER__;
    } catch { return false; }
  }, []);

  useEffect(() => {
    if (isOpen && hasSession) {
      axios.get(`${API_BASE}/api/history/providers`, { withCredentials: true })
        .then(res => {
          setProviders(res.data.providers || []);
          setUntaggedCount(res.data.untagged_count || 0);
        })
        .catch(() => {});
    }
  }, [isOpen, API_BASE, hasSession]);

  // Fetch archived summary whenever the panel opens (or after an archive op).
  const refreshArchived = useCallback(() => {
    if (!hasSession) return;
    axios.get(`${API_BASE}/api/history/archived`, { withCredentials: true })
      .then(res => {
        setArchivedTotal(res.data.total || 0);
        setArchivedEras(res.data.eras || []);
      })
      .catch(() => { setArchivedTotal(0); setArchivedEras([]); });
  }, [API_BASE, hasSession]);

  useEffect(() => {
    if (isOpen) refreshArchived();
  }, [isOpen, refreshArchived]);

  // Scroll-to-date when calendar input changes. Maps YYYY-MM-DD to the
  // localized date-header string used for [data-testid="slide-date-group-..."]
  // and scrolls it into view with a brief highlight pulse so providers can
  // see surrounding context (no longer filters items out of view).
  useEffect(() => {
    if (!dateJump || !isOpen) return;
    const target = new Date(dateJump + 'T00:00:00');
    if (Number.isNaN(target.getTime())) return;
    const headerLabel = target.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    // Wait a tick so collapsed-dates / re-render settle
    const timeoutId = setTimeout(() => {
      // Make sure the date isn't collapsed
      setCollapsedDates(prev => {
        if (!prev.has(headerLabel)) return prev;
        const next = new Set(prev); next.delete(headerLabel); return next;
      });
      const el = document.querySelector(
        `[data-testid="slide-date-group-${CSS.escape(headerLabel)}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Brief highlight pulse on the row
        el.classList.add('ring-2', 'ring-amber-400/70', 'rounded-md');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-amber-400/70', 'rounded-md');
        }, 1800);
      } else {
        toast.info(`No encounters on ${headerLabel}`, { duration: 2500 });
      }
    }, 80);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateJump, isOpen]);
  
  // Filter history based on search and filter
  const filteredHistory = useMemo(() => {
    let filtered = history || [];
    
    // Apply provider filter
    if (providerFilter === 'untagged') {
      filtered = filtered.filter(h => !h.provider_name);
    } else if (providerFilter !== 'all') {
      filtered = filtered.filter(h => h.provider_name === providerFilter);
    }
    
    // Apply filter
    if (historyFilter === 'drafts') {
      filtered = filtered.filter(h => h.is_draft);
    } else if (historyFilter === 'completed') {
      // "Submitted to Genesis" — the real final state
      filtered = filtered.filter(h => h.completed || h.completed_and_submitted);
    } else if (historyFilter === 'unsubmitted') {
      // Generated but NOT yet submitted to Genesis — the action queue
      filtered = filtered.filter(h => !h.is_draft && !(h.completed || h.completed_and_submitted));
    } else if (historyFilter === 'finished') {
      filtered = filtered.filter(h => h.status === 'finished');
    } else if (historyFilter === 'today') {
      const today = new Date().toDateString();
      filtered = filtered.filter(h => new Date(h.timestamp || h.created_at).toDateString() === today);
    } else if (historyFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(h => new Date(h.timestamp || h.created_at) >= weekAgo);
    } else if (historyFilter === 'referrals') {
      filtered = filtered.filter(h => 
        (h.referrals && h.referrals.length > 0) || 
        (h.specialties && h.specialties.length > 0) ||
        (h.indication && !h.clinical_note && !h.admin_letter)
      );
    }
    
    // Apply search
    if (historySearchQuery?.trim()) {
      const query = historySearchQuery.toLowerCase();
      filtered = filtered.filter(h => 
        h.chief_complaint?.toLowerCase().includes(query) ||
        h.notes?.toLowerCase().includes(query) ||
        h.clinical_note?.toLowerCase().includes(query) ||
        h.patient_label?.toLowerCase().includes(query)
      );
    }

    // NOTE (May 5, 2026): `dateJump` no longer filters here. It is used by
    // the scroll-to-date effect below to jump the list to the matching date
    // (with brief highlight) instead of filtering surrounding context out.

    return filtered;
  }, [history, historyFilter, historySearchQuery, providerFilter]);
  
  // Group by date with smart dedup + version condensing
  const groupedHistory = useMemo(() => {
    // Step 1: Exact duplicate removal — same minute + same complaint + same first line = drop
    const seen = new Map();
    const deduped = [];
    filteredHistory.forEach(item => {
      const ts = new Date(item.timestamp || item.created_at);
      const timeKey = `${ts.getHours()}:${ts.getMinutes()}`;
      const complaint = (item.chief_complaint || item.reason_label || '').substring(0, 40);
      const firstLine = (item.clinical_note || item.admin_letter || '').substring(0, 60);
      const key = `${ts.toDateString()}-${timeKey}-${complaint}-${firstLine}`;
      if (!seen.has(key)) {
        seen.set(key, item);
        deduped.push(item);
      }
    });
    
    // Step 2: Version condensing — same patient (age+gender+date) = versions of one encounter
    // When any version is marked Submitted (completed), show THAT as the head; otherwise latest wins.
    // Grouping key now ignores chief complaint since providers often edit it between regens.
    const getPatientIdentity = (item) => {
      const age = item.patient_age || '';
      const gender = (item.patient_gender || '').substring(0, 1).toLowerCase();
      return `${age}-${gender}`;
    };
    const condensed = [];
    const used = new Set();
    const sorted = [...deduped].sort((a, b) => new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at));
    sorted.forEach((item, i) => {
      if (used.has(i)) return;
      const ts = new Date(item.timestamp || item.created_at);
      const dateKey = ts.toDateString();
      const patId = getPatientIdentity(item);
      // Skip grouping if no patient identity (no age)
      if (!item.patient_age) { used.add(i); condensed.push(item); return; }
      // Find all items from same date, same patient identity
      const cluster = [item];
      for (let j = i + 1; j < sorted.length; j++) {
        if (used.has(j)) continue;
        const other = sorted[j];
        if (!other.patient_age) continue;
        const otherDate = new Date(other.timestamp || other.created_at).toDateString();
        if (otherDate !== dateKey) continue;
        if (getPatientIdentity(other) !== patId) continue;
        cluster.push(other);
        used.add(j);
      }
      used.add(i);
      if (cluster.length >= 2) {
        // Prefer the Submitted version as the head of the cluster; fall back to latest
        const submittedHead = cluster.find(c => c.completed || c.completed_and_submitted);
        const head = submittedHead || item;
        const rest = cluster.filter(c => c.id !== head.id);
        condensed.push({ ...head, _batchItems: [head, ...rest], _batchCount: cluster.length, _hasSubmitted: !!submittedHead });
      } else {
        condensed.push(item);
      }
    });
    
    const groups = {};
    condensed.forEach(item => {
      const date = new Date(item.timestamp || item.created_at).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
    });

    // Apply sort order — affects both day-group order AND items within each day.
    // Newest-first ('desc') is the default; provider can flip to 'asc' for
    // chronological review without entering bulk mode.
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const dA = new Date(a).getTime();
      const dB = new Date(b).getTime();
      return sortOrder === 'asc' ? dA - dB : dB - dA;
    });
    const sortedGroups = {};
    sortedKeys.forEach((date) => {
      const items = [...groups[date]].sort((a, b) => {
        const tA = new Date(a.timestamp || a.created_at).getTime();
        const tB = new Date(b.timestamp || b.created_at).getTime();
        return sortOrder === 'asc' ? tA - tB : tB - tA;
      });
      sortedGroups[date] = items;
    });
    return sortedGroups;
  }, [filteredHistory, sortOrder]);

  // Feb 18, 2026 — High-level date buckets. Each YYYY-derived date key from
  // groupedHistory is labeled with a coarse bucket ("Today", "Yesterday",
  // "This Week", "This Month", "Older") so providers can scan the panel by
  // recency without reading every date string.
  const dateBuckets = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const startOfWeek = new Date(today); startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const bucketOf = (dateStr) => {
      const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
      if (d.getTime() === today.getTime()) return 'Today';
      if (d.getTime() === yesterday.getTime()) return 'Yesterday';
      if (d >= startOfWeek) return 'This Week';
      if (d >= startOfMonth) return 'This Month';
      return 'Older';
    };
    const buckets = { 'Today': [], 'Yesterday': [], 'This Week': [], 'This Month': [], 'Older': [] };
    Object.entries(groupedHistory).forEach(([dateStr, items]) => {
      const b = bucketOf(dateStr);
      buckets[b].push({ dateStr, items });
    });
    return buckets;
  }, [groupedHistory]);

  // Track which buckets are collapsed (default: only Older collapsed)
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => new Set(['Older']));
  const toggleBucket = (bucket) => {
    setCollapsedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket); else next.add(bucket);
      return next;
    });
  };
  
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  
  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  const copyToClipboard = async (text) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Copied to clipboard');
      } catch (fallbackErr) {
        toast.error('Copy failed - try selecting text manually');
      }
    }
  };

  /**
   * When a provider marks a version as Submitted to Genesis, offer to cleanly delete
   * the sibling draft versions of the same encounter — the submitted copy is now the
   * source of truth, the iteration-drafts are noise. Uses an undo-toast pattern so
   * nothing is deleted silently.
   */
  const handleSubmittedToggle = async (item, checked) => {
    if (!updateHistoryItem) return;
    // silent: green SUBMITTED badge IS the feedback — no toast spam
    await updateHistoryItem(item.id, { completed: !!checked }, { silent: true });
    // Sync the Encounter Stoplight model so the live stoplight + StoplightPills
    // across the app immediately reflect the new green/yellow state.
    try { setEncounterStatus(item.id, checked ? 'green' : (item.clinical_note ? 'amber' : 'yellow')); } catch { /* ignore */ }
    if (!checked) return;
    // Notify the Breath Check banner that a submission just happened
    try { window.dispatchEvent(new CustomEvent('writeright:note-submitted')); } catch { /* ignore */ }
    // Honor "don't ask again" preference
    if (localStorage.getItem('writeright_skip_sibling_cleanup') === 'true') return;
    // Find sibling versions of this encounter (same age+gender, same date, not this id)
    const ts = new Date(item.timestamp || item.created_at);
    const dateKey = ts.toDateString();
    const age = item.patient_age;
    const gender = (item.patient_gender || '').substring(0, 1).toLowerCase();
    if (!age) return; // can't reliably group — skip offer
    const siblings = (history || []).filter(h => {
      if (!h.id || h.id === item.id) return false;
      if (h.completed || h.completed_and_submitted) return false; // keep other submitted versions
      if (!h.patient_age || h.patient_age !== age) return false;
      if ((h.patient_gender || '').substring(0, 1).toLowerCase() !== gender) return false;
      const hts = new Date(h.timestamp || h.created_at);
      return hts.toDateString() === dateKey;
    });
    if (siblings.length === 0) return;
    // Offer a single confirm — clear language, easy to cancel
    const siblingIds = siblings.map(s => s.id);
    toast('Keep only the Submitted version?', {
      description: `Delete ${siblings.length} older draft version${siblings.length > 1 ? 's' : ''} of this encounter. The Submitted copy stays.`,
      duration: 8000,
      action: {
        label: `Delete ${siblings.length}`,
        onClick: async () => {
          const API = window.__WR_API_BASE__;
          let deleted = 0;
          for (const sid of siblingIds) {
            try {
              await axios.delete(`${API}/api/history/${sid}`, { withCredentials: true });
              deleted++;
            } catch { /* ignore individual failures */ }
          }
          if (loadHistory) loadHistory();
          toast.success(`Cleaned up ${deleted} older version${deleted !== 1 ? 's' : ''}`);
        },
      },
      cancel: {
        label: "Don't ask again",
        onClick: () => {
          localStorage.setItem('writeright_skip_sibling_cleanup', 'true');
          toast.info('Sibling cleanup prompt disabled. Re-enable in Settings.', { duration: 2500 });
        },
      },
    });
  };

  // ───── BULK MODE HELPERS ─────
  // The per-row Submitted-to-Genesis checkboxes are intentionally separate from
  // bulk selection — they're for confirming individual notes hit Genesis.
  // Bulk mode lets the provider sweep through 50 old drafts at once.
  const visibleIdsForBulk = filteredHistory.map(h => h.id).filter(Boolean);
  const toggleBulkId = (id) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setBulkSelectedIds(new Set(visibleIdsForBulk));
  const clearBulkSelection = () => setBulkSelectedIds(new Set());
  const exitBulkMode = () => { setBulkMode(false); clearBulkSelection(); };

  const bulkMarkSubmitted = async () => {
    if (bulkSelectedIds.size === 0) return;
    const ids = Array.from(bulkSelectedIds);
    const API = window.__WR_API_BASE__;
    let ok = 0;
    for (const id of ids) {
      try {
        await axios.patch(`${API}/api/history/${id}`, { completed: true }, { withCredentials: true });
        ok++;
      } catch { /* continue */ }
    }
    if (loadHistory) loadHistory();
    try { window.dispatchEvent(new CustomEvent('writeright:note-submitted')); } catch { /* ignore */ }
    toast.success(`Marked ${ok} note${ok !== 1 ? 's' : ''} as Submitted to Genesis`);
    clearBulkSelection();
  };

  const bulkDelete = async () => {
    if (bulkSelectedIds.size === 0) return;
    const ids = Array.from(bulkSelectedIds);
    const confirmed = window.confirm(
      `Delete ${ids.length} selected history item${ids.length > 1 ? 's' : ''} permanently? This cannot be undone.`
    );
    if (!confirmed) return;
    const API = window.__WR_API_BASE__;
    let ok = 0;
    for (const id of ids) {
      try {
        await axios.delete(`${API}/api/history/${id}`, { withCredentials: true });
        ok++;
      } catch { /* continue */ }
    }
    if (loadHistory) loadHistory();
    toast.success(`Deleted ${ok} item${ok !== 1 ? 's' : ''}`);
    clearBulkSelection();
  };

  // One-click end-of-clinic-day: auto-select today's unsubmitted (non-draft) notes,
  // confirm count, bulk PATCH them all to Submitted.
  const submitAllTodaysUnsubmitted = async () => {
    const todayStr = new Date().toDateString();
    const targets = (history || []).filter((h) => {
      if (!h.id) return false;
      if (h.is_draft) return false;
      if (h.completed || h.completed_and_submitted) return false;
      const ts = new Date(h.timestamp || h.created_at);
      return ts.toDateString() === todayStr;
    });
    if (targets.length === 0) {
      toast.info('Nothing to submit — no unsubmitted notes from today');
      return;
    }
    const confirmed = window.confirm(
      `Mark all ${targets.length} of today's unsubmitted note${targets.length > 1 ? 's' : ''} as Submitted to MHS Genesis?\n\nOnly check this after the notes are actually in the real chart.`
    );
    if (!confirmed) return;
    const API = window.__WR_API_BASE__;
    let ok = 0;
    for (const t of targets) {
      try {
        await axios.patch(`${API}/api/history/${t.id}`, { completed: true }, { withCredentials: true });
        ok++;
      } catch { /* continue */ }
    }
    if (loadHistory) loadHistory();
    try { window.dispatchEvent(new CustomEvent('writeright:note-submitted')); } catch { /* ignore */ }
    toast.success(`Marked ${ok} of today's note${ok !== 1 ? 's' : ''} as Submitted`);
    clearBulkSelection();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Slide-in Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[500px] lg:w-[600px] bg-background border-l border-border z-50 shadow-2xl transform transition-transform duration-300 flex flex-col history-slide-panel ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-history-scale={textSizeLevel || 0}
      >
        {/* Header */}
        <div className="shrink-0 bg-card border-b border-border p-4 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              All History
              <span className="text-sm font-normal text-muted-foreground">
                ({filteredHistory.length} items)
              </span>
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search notes, complaints..."
              value={historySearchQuery || ''}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
              className="pl-9 h-9 bg-input"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'All' },
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'referrals', label: 'Referrals' },
              { value: 'drafts', label: 'Drafts' },
              { value: 'finished', label: 'Finished' },
              { value: 'unsubmitted', label: 'Needs Submit', accent: 'amber' },
              { value: 'completed', label: 'Submitted ✓', accent: 'emerald' }
            ].map(filter => {
              const isActive = historyFilter === filter.value;
              const activeCls = filter.accent === 'emerald'
                ? 'bg-emerald-500 text-white'
                : filter.accent === 'amber'
                  ? 'bg-amber-500 text-white'
                  : 'bg-primary text-primary-foreground';
              return (
                <button
                  key={filter.value}
                  onClick={() => setHistoryFilter(filter.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive ? activeCls : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                  }`}
                  data-testid={`slide-filter-${filter.value}`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          {/* Date-jump + Sort toggle row */}
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {/* Calendar date jump */}
            <label
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-secondary/50 text-muted-foreground hover:bg-secondary cursor-pointer transition-colors"
              data-testid="slide-date-jump-label"
            >
              <CalendarSearch className="w-3.5 h-3.5" />
              <span>Jump to date:</span>
              <input
                type="date"
                value={dateJump}
                onChange={(e) => setDateJump(e.target.value)}
                className="bg-transparent text-foreground text-xs outline-none border-none p-0 cursor-pointer [color-scheme:dark]"
                data-testid="slide-date-jump-input"
              />
              {dateJump && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setDateJump(''); }}
                  className="ml-1 text-amber-400 hover:text-amber-300 leading-none"
                  title="Clear date filter"
                  data-testid="slide-date-jump-clear"
                >
                  ✕
                </button>
              )}
            </label>

            {/* Sort order toggle */}
            <button
              type="button"
              onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-secondary/50 text-muted-foreground hover:bg-secondary transition-colors"
              data-testid="slide-sort-toggle"
              title={sortOrder === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
            >
              {sortOrder === 'desc' ? <ArrowDownAZ className="w-3.5 h-3.5" /> : <ArrowUpZA className="w-3.5 h-3.5" />}
              <span>{sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}</span>
            </button>

            {/* Mar 3, 2026 — Career-transition archive pill. Bulk-archives the
                current era of history (hidden from list + tally reset) with a
                one-click Restore path via the Archived button. */}
            <button
              type="button"
              onClick={() => setShowArchiveConfirm(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
              data-testid="history-archive-era-btn"
              title="Bulk-archive your current era of history and reset your personal tally. Fully recoverable."
            >
              <span aria-hidden>📦</span>
              <span>Start new era…</span>
            </button>

            {archivedTotal > 0 && (
              <button
                type="button"
                onClick={() => setShowArchivedPanel((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  showArchivedPanel
                    ? 'bg-slate-500/20 text-slate-100 border-slate-400/60'
                    : 'bg-slate-500/10 text-slate-300 border-slate-500/30 hover:bg-slate-500/20'
                }`}
                data-testid="history-view-archived-btn"
                title="Browse and restore archived encounters"
              >
                <span aria-hidden>🗂️</span>
                <span>Archived ({archivedTotal})</span>
              </button>
            )}
          </div>

          {/* ───── Bulk Mode Toolbar ───── */}
          <div className="mt-3 pt-2 border-t border-border/50">
            {!bulkMode ? (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button
                  onClick={() => setBulkMode(true)}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
                  data-testid="slide-enter-bulk-mode"
                >
                  <ListChecks className="w-3.5 h-3.5" />
                  Bulk select &amp; act
                </button>
                {/* One-click: submit all today's unsubmitted — no bulk-mode needed */}
                <button
                  onClick={submitAllTodaysUnsubmitted}
                  className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30 font-semibold"
                  data-testid="slide-submit-all-today"
                  title="Auto-select and mark today's unsubmitted notes as Submitted to MHS Genesis — for end-of-clinic-day"
                >
                  <CheckSquare className="w-3.5 h-3.5" />
                  Submit Today's Notes
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <ListChecks className="w-3.5 h-3.5 text-amber-400" />
                    <span className="font-semibold">Bulk Mode</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-amber-400 font-bold">{bulkSelectedIds.size}</span>
                    <span className="text-muted-foreground">selected</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[11px] px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={submitAllTodaysUnsubmitted}
                      data-testid="slide-bulk-submit-today"
                      title="Auto-select and mark today's unsubmitted notes as Submitted"
                    >
                      Today's Notes →
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[11px] px-2"
                      onClick={bulkSelectedIds.size === visibleIdsForBulk.length ? clearBulkSelection : selectAllVisible}
                      data-testid="slide-bulk-select-all"
                    >
                      {bulkSelectedIds.size === visibleIdsForBulk.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[11px] px-2 text-muted-foreground hover:text-foreground"
                      onClick={exitBulkMode}
                      data-testid="slide-bulk-exit"
                    >
                      Exit
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-initial"
                    disabled={bulkSelectedIds.size === 0}
                    onClick={bulkMarkSubmitted}
                    data-testid="slide-bulk-mark-submitted"
                  >
                    <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                    Mark Submitted ({bulkSelectedIds.size})
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-8 text-xs flex-1 sm:flex-initial"
                    disabled={bulkSelectedIds.size === 0}
                    onClick={bulkDelete}
                    data-testid="slide-bulk-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete ({bulkSelectedIds.size})
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Provider Filter */}
          {(providers.length > 0 || untaggedCount > 0) && (
            <div className="flex gap-2 flex-wrap mt-2 items-center">
              <User className="w-3 h-3 text-muted-foreground shrink-0" />
              <button
                onClick={() => setProviderFilter('all')}
                className={`px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                  providerFilter === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                }`}
              >All Providers</button>
              {providers.map(p => (
                <button
                  key={p}
                  onClick={() => setProviderFilter(p)}
                  className={`px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                    providerFilter === p
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                  }`}
                >{p}</button>
              ))}
              {untaggedCount > 0 && (
                <button
                  onClick={() => setProviderFilter('untagged')}
                  className={`px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                    providerFilter === 'untagged'
                      ? 'bg-amber-600 text-white'
                      : 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50'
                  }`}
                >Untagged ({untaggedCount})</button>
              )}
            </div>
          )}
        </div>
        
        {/* History List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Provider to-do scratchpad — for charting work that doesn't yet
              have a history row (meds to pull from yesterday, profiles to
              write, follow-ups). Persists in localStorage. Feb 18, 2026. */}
          <HistoryTodoList variant="slide" />

          {/* Column legend — clarifies the two checkboxes per row */}
          {Object.keys(groupedHistory).length > 0 && (
            <div className="flex items-center gap-2 px-1 -mb-2 text-[10px] uppercase tracking-wider text-muted-foreground/70 select-none" data-testid="history-column-legend">
              {bulkMode && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm border border-amber-400/60" />
                  <span>Pick</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-2.5 w-2.5 rounded-sm border border-emerald-500/60" />
                <span>Submitted to Genesis</span>
              </span>
              <span className="ml-auto text-[10px] text-muted-foreground/50">{bulkMode ? 'Pick = bulk select  ·  Submitted = mark in EHR' : 'Tap row to expand  ·  ✓ when note is in MHS Genesis'}</span>
            </div>
          )}
          {Object.keys(groupedHistory).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No history found</p>
              {historySearchQuery && (
                <p className="text-sm mt-1">Try a different search term</p>
              )}
            </div>
          ) : (
            // Feb 18, 2026 — Coarse date buckets wrap the fine-grained date groups.
            // Today / Yesterday / This Week / This Month / Older. Older starts collapsed.
            Object.entries(dateBuckets).map(([bucketName, bucketGroups]) => {
              if (bucketGroups.length === 0) return null;
              const bucketCollapsed = collapsedBuckets.has(bucketName);
              const totalInBucket = bucketGroups.reduce((sum, g) => sum + g.items.length, 0);
              const bucketColor = {
                'Today':      'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
                'Yesterday':  'text-sky-400 border-sky-500/30 bg-sky-500/5',
                'This Week':  'text-amber-400 border-amber-500/30 bg-amber-500/5',
                'This Month': 'text-purple-300 border-purple-500/30 bg-purple-500/5',
                'Older':      'text-muted-foreground border-border bg-secondary/30',
              }[bucketName] || '';
              return (
                <div key={bucketName} className="space-y-1.5" data-testid={`history-bucket-${bucketName.toLowerCase().replace(/\s+/g, '-')}`}>
                  <button
                    onClick={() => toggleBucket(bucketName)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border ${bucketColor} sticky top-0 z-20 backdrop-blur-sm hover:opacity-90 transition-opacity`}
                    data-testid={`history-bucket-toggle-${bucketName.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${bucketCollapsed ? '-rotate-90' : ''}`} />
                    <span className="text-xs font-bold uppercase tracking-wider flex-1 text-left">{bucketName}</span>
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-semibold">{totalInBucket}</Badge>
                  </button>
                  {!bucketCollapsed && bucketGroups.map(({ dateStr, items }) => {
                    // Bulk-close-day for the slide panel — Feb 24, 2026.
                    // Mirrors the inline HistoryPanel behavior: tally how many
                    // items in this date are unsigned, render either a
                    // 'Close N' (enabled) or 'All Closed' (disabled) button,
                    // and loop handleSubmittedToggle to flip them. Updates
                    // live without reload.
                    const unsignedInGroup = items.filter(i => !(i.completed || i.completed_and_submitted) && !i._batchItems);
                    const allClosed = unsignedInGroup.length === 0;
                    const handleBulkCloseDay = async (e) => {
                      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                      if (allClosed) return;
                      const n = unsignedInGroup.length;
                      const toastId = toast.loading(`Closing ${n} note${n !== 1 ? 's' : ''}…`);
                      let okCount = 0;
                      for (const itm of unsignedInGroup) {
                        try { await handleSubmittedToggle(itm, true); okCount += 1; }
                        catch { /* ignore */ }
                      }
                      toast.dismiss(toastId);
                      toast.success(`Closed ${okCount} note${okCount !== 1 ? 's' : ''} on ${dateStr}`, { duration: 2500 });
                    };
                    return (
                    <div key={dateStr} className="space-y-2 pl-2">
                      {/* Date Header Row — collapse toggle + bulk-close */}
                      <div className="w-full flex items-center gap-2 py-1.5">
                        <button
                          onClick={() => setCollapsedDates(prev => {
                            const next = new Set(prev);
                            if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
                            return next;
                          })}
                          className="flex items-center gap-2 flex-1 hover:text-foreground transition-colors"
                          data-testid={`slide-date-group-${dateStr}`}
                        >
                          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${collapsedDates.has(dateStr) ? '-rotate-90' : ''}`} />
                          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">{dateStr}</span>
                          <Badge variant="outline" className="text-[9px] h-3.5 px-1">{items.length}</Badge>
                          {unsignedInGroup.length > 0 && (
                            <Badge
                              variant="outline"
                              className="text-[9px] h-3.5 px-1 border-amber-500/50 text-amber-300 bg-amber-500/10"
                              data-testid={`slide-date-group-unsigned-${dateStr}`}
                            >
                              {unsignedInGroup.length} unsigned
                            </Badge>
                          )}
                        </button>
                        <button
                          onClick={handleBulkCloseDay}
                          disabled={allClosed}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border transition-colors ${
                            allClosed
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300/70 cursor-default'
                              : 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400/70 cursor-pointer'
                          }`}
                          title={allClosed ? `All ${items.length} notes on ${dateStr} already submitted` : `Mark all ${unsignedInGroup.length} unsigned notes on ${dateStr} as Submitted to Genesis`}
                          data-testid={`slide-bulk-close-day-${dateStr}`}
                        >
                          <Check className="w-3 h-3" />
                          <span>{allClosed ? 'All Closed' : `Close ${unsignedInGroup.length}`}</span>
                        </button>
                      </div>

                      {/* Items for this date */}
                      {!collapsedDates.has(dateStr) && items.map((item) => {
                  // Batch group rendering — condense 3+ same-type items into expandable row
                  if (item._batchItems && item._batchCount) {
                    const batchKey = `batch-${item.id}`;
                    const isExpanded = expandedBatches.has(batchKey);
                    const typeLabel = item.letter_type || item.admin_letter ? 'Letters/Referrals' : 'Notes';
                    return (
                      <div key={batchKey} className={`bg-card rounded-lg border transition-all ${item._hasSubmitted ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                        <button
                          onClick={() => setExpandedBatches(prev => {
                            const next = new Set(prev);
                            if (next.has(batchKey)) next.delete(batchKey); else next.add(batchKey);
                            return next;
                          })}
                          className="w-full p-3 text-left flex items-center gap-3"
                          data-testid={`batch-group-${item.id}`}
                        >
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''} ${item._hasSubmitted ? 'text-emerald-400' : 'text-amber-400'}`} />
                          <Clock className={`w-4 h-4 ${item._hasSubmitted ? 'text-emerald-400' : 'text-amber-400'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-foreground truncate">
                                {getHistoryDisplayTitle(item)}
                              </span>
                              {item._hasSubmitted && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 shrink-0">
                                  SUBMITTED
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${item._hasSubmitted ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}>
                                {item._batchCount} versions
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">
                                {formatHistoryTime(item.created_at || item.timestamp, item.patient_label)}
                              </span>
                              {item.patient_age && (
                                <span className="text-[10px] text-muted-foreground">
                                  {item.patient_age}yo {item.patient_gender || ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className={`px-3 pb-3 space-y-1.5 border-t pt-2 ${item._hasSubmitted ? 'border-emerald-500/20' : 'border-amber-500/20'}`}>
                            {item._batchItems.map((bItem, bIdx) => (
                              <div key={bItem.id} className={`flex items-center gap-2 p-2 rounded transition-colors ${bIdx === 0 && item._hasSubmitted ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-secondary/30 hover:bg-secondary/50'}`}>
                                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={(bItem.completed || bItem.completed_and_submitted) || false}
                                    onCheckedChange={(checked) => handleSubmittedToggle(bItem, !!checked)}
                                    className={`h-4 w-4 border-2 ${(bItem.completed || bItem.completed_and_submitted) ? 'border-emerald-500 bg-emerald-500/30' : 'border-muted-foreground/40'}`}
                                    title={(bItem.completed || bItem.completed_and_submitted) ? 'Unmark — remove Submitted to Genesis' : 'Submitted to MHS Genesis'}
                                    data-testid={`batch-item-checkbox-${bItem.id}`}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium truncate ${(bItem.completed || bItem.completed_and_submitted) ? 'text-emerald-300' : 'text-foreground'}`}>
                                    {getHistoryDisplayTitle(bItem)}
                                    {(bItem.completed || bItem.completed_and_submitted) && (
                                      <span className="ml-1.5 text-[9px] font-bold text-emerald-400">✓ SUBMITTED</span>
                                    )}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground truncate">
                                    {bItem.chief_complaint && `CC: ${bItem.chief_complaint}`}
                                    {bItem.patient_age && ` | ${bItem.patient_age}yo ${bItem.patient_gender || ''}`}
                                    {bItem.submitted_at && ` | Submitted ${new Date(bItem.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-[10px] shrink-0">{getDocTypeLabel(bItem, effectiveRole)}</Badge>
                                <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => { loadHistoryItem(bItem); onClose(); }}>
                                  <Eye className="w-3 h-3 mr-1" />Load
                                </Button>
                                {bItem.clinical_note && (
                                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => copyToClipboard(bItem.clinical_note)}>
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Regular single item rendering
                  return (
                  <div
                    key={item.id}
                    className={`bg-card rounded-lg border transition-all ${
                      (item.completed || item.completed_and_submitted)
                        ? 'border-emerald-500/40 bg-emerald-500/5 opacity-70'
                        : item.status === 'finished'
                          ? 'border-green-500/30 bg-green-500/5'
                          : item.is_draft 
                            ? 'border-blue-500/30 bg-blue-500/5' 
                            : 'border-border hover:border-primary/30'
                    }`}
                  >
                    {/* Item Header */}
                    <HoverCard openDelay={300} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <button
                          onClick={() => bulkMode ? toggleBulkId(item.id) : toggleExpand(item.id)}
                          className="w-full p-3 text-left flex items-start gap-3"
                          data-testid={`history-item-${item.id}`}
                        >
                      {/* Bulk-Mode selection checkbox (separate from Submitted-to-Genesis) */}
                      {bulkMode && (
                        <div className="flex-shrink-0 mt-0.5" onClick={(e) => { e.stopPropagation(); toggleBulkId(item.id); }}>
                          <div
                            className={`h-5 w-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                              bulkSelectedIds.has(item.id) ? 'border-amber-400 bg-amber-400/30' : 'border-amber-400/40 hover:border-amber-400/70'
                            }`}
                            data-testid={`slide-bulk-checkbox-${item.id}`}
                          >
                            {bulkSelectedIds.has(item.id) && <Check className="w-3.5 h-3.5 text-amber-300" />}
                          </div>
                        </div>
                      )}
                      {/* Submitted to MHS Genesis checkbox */}
                      <div className="flex-shrink-0 mt-0.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={item.completed || item.completed_and_submitted || false}
                          onCheckedChange={(checked) => handleSubmittedToggle(item, !!checked)}
                          className={`h-5 w-5 border-2 ${(item.completed || item.completed_and_submitted) ? 'border-emerald-500 bg-emerald-500/30' : 'border-muted-foreground/40'}`}
                          data-testid={`slide-completed-${item.id}`}
                          title={(item.completed || item.completed_and_submitted) ? 'Unmark — remove Submitted to Genesis' : 'Submitted to MHS Genesis — check when note is in the real chart'}
                        />
                      </div>
                      {/* Time stamp - prominent left column */}
                      <div className="flex-shrink-0 min-w-[60px] text-right pt-0.5">
                        <span className="text-sm font-bold text-primary tabular-nums" data-testid="history-item-time">
                          {formatHistoryTime(item.created_at || item.timestamp, item.patient_label)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Note Title — own line, prominent */}
                        <p className="text-sm font-semibold text-foreground line-clamp-1 mb-0.5" data-testid="history-item-title">
                          {getHistoryDisplayTitle(item)}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <StoplightPill
                            encounterId={item.id}
                            fallbackComplete={!!(item.clinical_note)}
                            fallbackSubmitted={!!(item.completed || item.completed_and_submitted)}
                          />
                          {item.is_draft && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">
                              DRAFT
                            </span>
                          )}
                          {(item.completed || item.completed_and_submitted) && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/25 text-emerald-300 border border-emerald-500/40" title={item.submitted_at ? `Submitted to MHS Genesis · ${new Date(item.submitted_at).toLocaleString()}${item.submitted_by ? ' · ' + item.submitted_by : ''}` : 'Submitted to MHS Genesis'} data-testid="history-submitted-badge">
                              SUBMITTED
                            </span>
                          )}
                          {item.status === 'finished' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">
                              FINISHED
                            </span>
                          )}
                          <Badge variant="outline" className="text-[10px] bg-secondary/60 border-border shrink-0" data-testid="history-item-doctype">
                            {getDocTypeLabel(item, effectiveRole)}
                          </Badge>
                          {item.patient_age && (
                            <span className="text-[10px] text-muted-foreground">{item.patient_age}yo {item.patient_gender}</span>
                          )}
                          {item.chief_complaint && (
                            <span className="text-[10px] text-muted-foreground/70 truncate max-w-[150px]">CC: {item.chief_complaint}</span>
                          )}
                          {item.provider_name && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400" data-testid="history-item-provider">
                              {item.provider_name}
                            </span>
                          )}
                          {/* Critical label badge — stands out for quick scanning */}
                          {item.patient_label && (() => {
                            // Extract custom label part (after date/time/slot number)
                            const label = item.patient_label;
                            const customPart = label.replace(/^\d{1,2}\s\w{3}\s(?:#\d+\s)?(?:\d{1,2}:\d{2}\s(?:AM|PM)\s?)?/i, '').trim();
                            return customPart ? (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/25 text-amber-300 border border-amber-500/40" data-testid="history-item-label">
                                {customPart}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </button>
                      </HoverCardTrigger>
                      <HoverCardContent side="left" align="start" className="w-80 p-3 text-xs space-y-2" data-testid="history-hover-preview">
                        <p className="font-bold text-foreground text-sm">{getHistoryDisplayTitle(item)}</p>
                        {(item.patient_age || item.patient_gender) && (
                          <p className="text-muted-foreground">
                            <span className="font-semibold text-foreground/80">Patient:</span> {item.patient_age ? `${item.patient_age}yo` : ''} {item.patient_gender || ''}
                          </p>
                        )}
                        {item.chief_complaint && (
                          <p className="text-muted-foreground"><span className="font-semibold text-foreground/80">CC:</span> {item.chief_complaint}</p>
                        )}
                        {item.notes && (
                          <div className="text-muted-foreground max-h-[80px] overflow-hidden">
                            <span className="font-semibold text-foreground/80">Notes:</span> {item.notes.substring(0, 150)}{item.notes.length > 150 && '...'}
                          </div>
                        )}
                        {item.clinical_note && (
                          <div className="text-muted-foreground max-h-[80px] overflow-hidden border-t border-border/50 pt-1.5 mt-1.5">
                            <span className="font-semibold text-green-400/80">Note:</span> {(() => {
                              let preview = item.clinical_note;
                              // Skip the generic telehealth header — show the actual clinical content
                              const apIdx = preview.indexOf('A/P SUMMARY:');
                              const hpiIdx = preview.indexOf('HPI:');
                              if (apIdx > 0) preview = preview.substring(apIdx);
                              else if (hpiIdx > 0) preview = preview.substring(hpiIdx);
                              else if (preview.startsWith('TELEHEALTH VISIT:')) {
                                const nextLine = preview.indexOf('\n\n');
                                if (nextLine > 0) preview = preview.substring(nextLine + 2);
                              }
                              return preview.substring(0, 150) + (preview.length > 150 ? '...' : '');
                            })()}
                          </div>
                        )}
                        {effectiveRole !== 'patient' && item.icd10_suggestions?.length > 0 && (
                          <p className="text-muted-foreground font-mono">
                            <span className="font-semibold text-foreground/80">ICD-10:</span> {item.icd10_suggestions.map(s => typeof s === 'object' ? s.code : s).join(', ')}
                          </p>
                        )}
                        {item.is_draft && <p className="text-blue-400 italic">Draft — hover to preview, click to restore</p>}
                      </HoverCardContent>
                    </HoverCard>
                    
                    {/* Expanded Content */}
                    {expandedItems.has(item.id) && (
                      <div className="px-3 pb-3 pt-0 border-t border-border/50 space-y-3">
                        {/* Notes Preview */}
                        {item.notes && (
                          <div className="p-2 bg-secondary/30 rounded text-xs text-muted-foreground max-h-[100px] overflow-y-auto">
                            <p className="font-medium text-foreground/70 mb-1">Notes:</p>
                            {item.notes.substring(0, 200)}
                            {item.notes.length > 200 && '...'}
                          </div>
                        )}
                        
                        {/* Clinical Note Preview */}
                        {item.clinical_note && (
                          <div className="p-2 bg-green-500/10 rounded text-xs text-muted-foreground max-h-[100px] overflow-y-auto">
                            <p className="font-medium text-green-500/70 mb-1">Generated Note:</p>
                            {item.clinical_note.substring(0, 200)}
                            {item.clinical_note.length > 200 && '...'}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Quick-tag buttons */}
                          {!item.provider_name && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.post(`${API_BASE}/api/history/bulk-tag`, { provider_name: "Ma'am", entry_ids: [item.id] }, { withCredentials: true });
                                    item.provider_name = "Ma'am";
                                    toast.success("Tagged as Ma'am");
                                    if (typeof loadHistory === 'function') loadHistory();
                                  } catch { toast.error('Tag failed'); }
                                }}
                                className="h-7 text-xs text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
                                data-testid="tag-mine-btn"
                              >
                                <User className="w-3 h-3 mr-1" />
                                Ma'am
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.post(`${API_BASE}/api/history/bulk-tag`, { provider_name: 'CPT Brown', entry_ids: [item.id] }, { withCredentials: true });
                                    item.provider_name = 'CPT Brown';
                                    toast.success('Tagged as CPT Brown');
                                    if (typeof loadHistory === 'function') loadHistory();
                                  } catch { toast.error('Tag failed'); }
                                }}
                                className="h-7 text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                                data-testid="tag-tyren-btn"
                              >
                                <User className="w-3 h-3 mr-1" />
                                CPT Brown
                              </Button>
                            </>
                          )}
                          {item.provider_name && (
                            <span className="px-2 py-1 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400">
                              {item.provider_name}
                            </span>
                          )}
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                              loadHistoryItem(item);
                              onClose();
                              // Defer to next paint so the output panel has rendered,
                              // then scroll it into view — fixes the "Load only loads input" UX confusion
                              // (output WAS restored but lived offscreen below the slide panel close point).
                              setTimeout(() => {
                                const target = document.getElementById('output-section') ||
                                  document.querySelector('[data-testid="output-card"]') ||
                                  document.querySelector('[data-testid="clinical-note-output"]');
                                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 250);
                            }}
                            className="h-7 text-xs"
                            data-testid="slide-load-history-btn"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Load
                          </Button>
                          {item.clinical_note && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(item.clinical_note)}
                              className="h-7 text-xs"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy Note
                            </Button>
                          )}
                          {item.is_draft && markDraftsAsCompleted && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markDraftsAsCompleted([item.id])}
                              className="h-7 text-xs text-green-500 border-green-500/30 hover:bg-green-500/10"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Mark Done
                            </Button>
                          )}
                          {item.status !== 'finished' && !item.is_draft && updateHistoryItem && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateHistoryItem(item.id, { status: 'finished' })}
                              className="h-7 text-xs text-green-500 border-green-500/30 hover:bg-green-500/10"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Finished
                            </Button>
                          )}
                          {deleteHistoryItem && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm('Delete this item?')) {
                                  deleteHistoryItem(item.id);
                                }
                              }}
                              className="h-7 text-xs text-red-400 hover:text-red-500 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                    </div>
                  );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Mar 3, 2026 — Archive-Era Confirm Modal
          Bulk-marks all non-archived history for the user as archived with
          an era label. Personal /stats/provider aggregation excludes
          archived → tally naturally resets. Restorable via Archived panel.
         ═══════════════════════════════════════════════════════════════ */}
      {showArchiveConfirm && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !isArchiving && setShowArchiveConfirm(false)}
          data-testid="history-archive-confirm-overlay"
        >
          <div
            className="w-full max-w-md bg-card border border-amber-500/40 rounded-xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2 mb-3">
              <span aria-hidden>📦</span> Start a new era
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              This will archive <strong className="text-amber-200">{history?.length || 0}</strong> encounter{(history?.length || 0) === 1 ? '' : 's'} under the label below, hide them from your main list, and <strong className="text-amber-200">reset your personal tally to zero</strong> for the new role.
              <br /><br />
              Nothing is deleted — you can restore anytime from the <strong>Archived</strong> panel.
            </p>
            <label className="block text-xs text-muted-foreground mb-1.5 uppercase tracking-wider font-semibold">
              Era label
            </label>
            <Input
              value={archiveEraLabel}
              onChange={(e) => setArchiveEraLabel(e.target.value)}
              placeholder="e.g. Primary Care era"
              className="h-9 bg-input mb-4"
              data-testid="history-archive-era-input"
              disabled={isArchiving}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowArchiveConfirm(false)}
                disabled={isArchiving}
                data-testid="history-archive-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const label = (archiveEraLabel || '').trim() || 'Archived';
                  setIsArchiving(true);
                  try {
                    const res = await axios.post(
                      `${API_BASE}/api/history/archive-all`,
                      { era_label: label },
                      { withCredentials: true }
                    );
                    const n = res.data?.archived ?? 0;
                    toast.success(`Archived ${n} encounter${n === 1 ? '' : 's'} under "${label}". Tally reset.`, {
                      duration: 5000,
                      action: {
                        label: 'Undo',
                        onClick: async () => {
                          try {
                            await axios.post(
                              `${API_BASE}/api/history/restore-all?era=${encodeURIComponent(label)}`,
                              {},
                              { withCredentials: true }
                            );
                            toast.success(`Restored "${label}"`);
                            if (loadHistory) loadHistory();
                            refreshArchived();
                          } catch (_) {
                            toast.error('Undo failed — try Archived panel');
                          }
                        },
                      },
                    });
                    setShowArchiveConfirm(false);
                    if (loadHistory) loadHistory();
                    refreshArchived();
                  } catch (e) {
                    toast.error('Archive failed — please try again');
                  } finally {
                    setIsArchiving(false);
                  }
                }}
                disabled={isArchiving}
                className="bg-amber-500 text-black hover:bg-amber-400"
                data-testid="history-archive-confirm"
              >
                {isArchiving ? 'Archiving…' : 'Archive & Reset Tally'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Archived-eras slide-out panel — inline drawer inside the history panel */}
      {showArchivedPanel && (
        <div
          className="absolute top-0 right-0 h-full w-full sm:w-[500px] lg:w-[600px] bg-background border-l border-slate-500/40 z-[55] shadow-2xl overflow-y-auto"
          data-testid="history-archived-panel"
        >
          <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
            <h3 className="text-base font-bold flex items-center gap-2">
              <span aria-hidden>🗂️</span> Archived Encounters
              <span className="text-xs text-muted-foreground font-normal">
                ({archivedTotal})
              </span>
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchivedPanel(false)}
              className="h-8 w-8 p-0"
              data-testid="history-archived-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="p-4 space-y-4">
            {archivedEras.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8">
                No archived encounters yet.
              </div>
            )}
            {archivedEras.map((eraGroup) => (
              <div
                key={eraGroup.era}
                className="rounded-lg border border-border p-3 bg-secondary/30"
                data-testid={`history-archived-era-${eraGroup.era}`}
              >
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-foreground truncate">{eraGroup.era}</div>
                    <div className="text-xs text-muted-foreground">
                      {eraGroup.count} encounter{eraGroup.count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={async () => {
                        const next = window.prompt(`Rename "${eraGroup.era}" to:`, eraGroup.era);
                        if (!next || next.trim() === eraGroup.era) return;
                        try {
                          const res = await axios.post(
                            `${API_BASE}/api/history/rename-era?old_era=${encodeURIComponent(eraGroup.era)}&new_era=${encodeURIComponent(next.trim())}`,
                            {},
                            { withCredentials: true }
                          );
                          toast.success(`Renamed to "${next.trim()}" (${res.data?.renamed ?? 0} encounters)`);
                          refreshArchived();
                        } catch (_) {
                          toast.error('Rename failed');
                        }
                      }}
                      className="text-[11px] text-slate-300 hover:text-slate-100 px-2 py-1 rounded hover:bg-slate-500/20 transition-colors"
                      data-testid={`history-archived-rename-era-${eraGroup.era}`}
                      title="Rename this era"
                    >
                      Rename
                    </button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await axios.post(
                            `${API_BASE}/api/history/restore-all?era=${encodeURIComponent(eraGroup.era)}`,
                            {},
                            { withCredentials: true }
                          );
                          const n = res.data?.restored ?? 0;
                          toast.success(`Restored ${n} encounter${n === 1 ? '' : 's'} from "${eraGroup.era}"`);
                          if (loadHistory) loadHistory();
                          refreshArchived();
                        } catch (_) {
                          toast.error('Restore failed — please retry');
                        }
                      }}
                      className="h-7 text-xs"
                      data-testid={`history-archived-restore-era-${eraGroup.era}`}
                    >
                      Restore era
                    </Button>
                  </div>
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {eraGroup.entries.slice(0, 20).map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-background/50"
                    >
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium truncate">
                          {e.patient_label || e.chief_complaint || '(no title)'}
                        </div>
                        <div className="text-muted-foreground text-[10px]">
                          {e.patient_age ? `${e.patient_age}yo ` : ''}{e.patient_gender || ''} · {e.archived_at ? new Date(e.archived_at).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await axios.post(
                              `${API_BASE}/api/history/${e.id}/restore`,
                              {},
                              { withCredentials: true }
                            );
                            toast.success('Encounter restored');
                            if (loadHistory) loadHistory();
                            refreshArchived();
                          } catch (_) {
                            toast.error('Restore failed');
                          }
                        }}
                        className="text-emerald-400 hover:text-emerald-300 text-[11px] font-medium px-2 py-1 rounded hover:bg-emerald-500/10"
                        title="Restore this encounter"
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                  {eraGroup.entries.length > 20 && (
                    <div className="text-[11px] text-muted-foreground pt-2 text-center">
                      + {eraGroup.entries.length - 20} more — use &ldquo;Restore era&rdquo; to bring back all
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default HistorySlidePanel;
